// AppShell (Spec §6.1) — one shell for all roles. Sidebar (role-adaptive,
// collapsible) + TopBar (workspace switcher, search, help, notifications,
// avatar) + Breadcrumb row + an #E8EFF4 content area with a max-width container.
// Replaces the Inventor/Collaborator/Admin layout split with a single adaptive
// surface driven by the active workspace.

import { useEffect, useState } from 'react'
import { Outlet } from 'react-router-dom'
import Sidebar from './Sidebar'
import TopBar from './TopBar'
import Breadcrumbs from './Breadcrumbs'
import { PageCrumb } from '../spine'
import ResourcesPanel from '../help/ResourcesPanel'
import { onResourcesPanelRequest } from '../help/helpBus'
import SupportWidget from '../support/SupportWidget'
import SupportErrorBoundary from '../ErrorBoundary'

const COLLAPSE_KEY = 'sidebarCollapsed'

export default function AppShell() {
  const [collapsed, setCollapsed] = useState<boolean>(() => {
    try {
      return localStorage.getItem(COLLAPSE_KEY) === '1'
    } catch {
      return false
    }
  })
  const [mobileOpen, setMobileOpen] = useState(false)
  const [helpOpen, setHelpOpen] = useState(false)
  const [helpTopic, setHelpTopic] = useState<string | undefined>(undefined)

  // A HelpPopover's "Learn more" link anywhere in the app asks this ONE
  // mounted Resources panel to open on its topic (Story #5305 Stream 14d).
  useEffect(() => {
    return onResourcesPanelRequest((topic) => {
      setHelpTopic(topic)
      setHelpOpen(true)
    })
  }, [])

  useEffect(() => {
    try {
      localStorage.setItem(COLLAPSE_KEY, collapsed ? '1' : '0')
    } catch {
      /* non-fatal */
    }
  }, [collapsed])

  // "?" opens Help & Resources (unless typing in a field).
  useEffect(() => {
    function onKey(e: KeyboardEvent) {
      const el = e.target as HTMLElement | null
      const typing = el && (el.tagName === 'INPUT' || el.tagName === 'TEXTAREA' || el.isContentEditable)
      if (e.key === '?' && !typing) {
        e.preventDefault()
        setHelpTopic(undefined)
        setHelpOpen(true)
      }
    }
    document.addEventListener('keydown', onKey)
    return () => document.removeEventListener('keydown', onKey)
  }, [])

  return (
    <div className="min-h-screen bg-[#E8EFF4]">
      <Sidebar
        collapsed={collapsed}
        onToggleCollapsed={() => setCollapsed((c) => !c)}
        mobileOpen={mobileOpen}
        onCloseMobile={() => setMobileOpen(false)}
      />

      <div className={`flex min-h-screen flex-col transition-[padding] duration-200 ${collapsed ? 'lg:pl-16' : 'lg:pl-64'}`}>
        <TopBar
          onOpenMobileNav={() => setMobileOpen(true)}
          onOpenHelp={() => {
            setHelpTopic(undefined)
            setHelpOpen(true)
          }}
        />

        <main className="flex-1">
          <div className="mx-auto w-full max-w-7xl px-4 py-6 sm:px-6 lg:px-8">
            {/* Wayfinding: exactly ONE crumb line. A graduated screen declares
                its own (PageCrumb); Breadcrumbs renders nothing then, so the
                two stacked rows the old shell produced are gone. */}
            <Breadcrumbs />
            <PageCrumb />
            {/* Error-report fallback boundary (Story #4613, Stream 8b): a page
                crash renders a degraded-but-safe "Report this problem" surface
                instead of a blank screen. */}
            <SupportErrorBoundary>
              <Outlet />
            </SupportErrorBoundary>
          </div>
        </main>
      </div>

      <ResourcesPanel open={helpOpen} onClose={() => setHelpOpen(false)} initialTopic={helpOpen ? helpTopic : undefined} />
      {/* Persistent floating support launcher on every authenticated screen. */}
      <SupportWidget />
    </div>
  )
}
